/**
 * Isolierte Public-Landingpage für kostenfreie Online-Events.
 * Route: /event/:slug — Patient Zero (kein Mandanten-/Seminar-/eventStore-Touch).
 * Daten: public_online_events/{slug} · Anmeldungen: online_event_anmeldungen
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, onSnapshot, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { PE_LOGO_SRC } from '../../constants/brandAssets';
import DsgvoModal from '../../components/DsgvoModal';
import { Calendar, Check, Clock, Loader2, AlertCircle, Video, Download } from 'lucide-react';
import { formatHalloGreeting, persistExternalLeadProfile, sanitizeFirstName } from '../../utils/mailGreeting';
import { clarifyOnlineFormat, clarifyOnlineWording } from '../../utils/onlineEventWording';
import { buildOnlineEventConfirmMail } from '../../utils/onlineEventConfirmMail';
import AdaptiveEventImage from '../../components/AdaptiveEventImage';
import EventRecordingBadge, { RecordingLiveOnlyNotice } from '../../components/EventRecordingBadge';

const GOLD = '#DCB68A';
const SLATE = '#0F172A';
const CARD = '#1E293B';
const DEEP = '#0A0A0A';

const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

interface Speaker {
  name: string;
  role?: string;
  imageUrl?: string;
  bio?: string;
}

interface OnlineEventData {
  title: string;
  subtitle?: string;
  date?: string;
  time?: string;
  format?: string;
  teaser?: string;
  bullets?: string[];
  heroImage?: string;
  speakers?: Speaker[];
  ctaLabel?: string;
  thankYouText?: string;
  isPublished?: boolean;
  activeDateISO?: string;
  accessUrl?: string;
  accessUrlEvening?: string;
  willBeRecorded?: boolean | null;
}

interface FormData {
  firstName: string;
  email: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_FORM: FormData = {
  firstName: '',
  email: '',
};

const inputClass =
  'w-full rounded-xl bg-[#0F172A] border border-slate-700 text-white px-4 py-3 text-sm focus:outline-none focus:border-[#DCB68A] focus:ring-1 focus:ring-[#DCB68A]/30 transition-colors placeholder:text-slate-600';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toIcsUtc(d: Date): string {
  return `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}${pad2(d.getUTCMinutes())}00Z`;
}

function escapeIcs(text: string): string {
  return String(text || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

function parseTimeParts(time?: string): { h: number; m: number } {
  const match = String(time || '').match(/(\d{1,2}):(\d{2})/);
  if (!match) return { h: 10, m: 0 };
  return { h: Number(match[1]), m: Number(match[2]) };
}

function parseEventStart(event: OnlineEventData): Date | null {
  const { h, m } = parseTimeParts(event.time);
  if (event.activeDateISO && /^\d{4}-\d{2}-\d{2}$/.test(event.activeDateISO)) {
    const [y, mo, d] = event.activeDateISO.split('-').map(Number);
    return new Date(y, mo - 1, d, h, m, 0);
  }
  const isoInDate = String(event.date || '').match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoInDate) {
    return new Date(Number(isoInDate[1]), Number(isoInDate[2]) - 1, Number(isoInDate[3]), h, m, 0);
  }
  const de = String(event.date || '').match(/(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})/);
  if (de) {
    return new Date(Number(de[3]), Number(de[2]) - 1, Number(de[1]), h, m, 0);
  }
  return null;
}

function splitDateLabel(date?: string, activeDateISO?: string): { weekday: string; dateLabel: string } {
  const raw = String(date || '').trim();
  for (const day of WEEKDAYS) {
    if (raw.toLowerCase().startsWith(day.toLowerCase())) {
      return { weekday: day, dateLabel: raw.slice(day.length).replace(/^,\s*/, '').trim() || raw };
    }
  }
  if (activeDateISO && /^\d{4}-\d{2}-\d{2}$/.test(activeDateISO)) {
    const [y, mo, d] = activeDateISO.split('-').map(Number);
    const dt = new Date(y, mo - 1, d);
    return {
      weekday: WEEKDAYS[dt.getDay()],
      dateLabel: dt.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }),
    };
  }
  return { weekday: '', dateLabel: raw };
}

function downloadEventIcs(slug: string, event: OnlineEventData) {
  const start = parseEventStart(event) || new Date();
  const end = new Date(start.getTime() + 90 * 60 * 1000);
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'CALSCALE:GREGORIAN',
    'PRODID:-//PreisExpert//Online-Event//DE',
    'BEGIN:VEVENT',
    `UID:event-${slug}@pe-one.friseur-coaching.de`,
    `DTSTAMP:${toIcsUtc(new Date())}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${escapeIcs(event.title)}`,
    `DESCRIPTION:${escapeIcs(clarifyOnlineWording(event.teaser || event.subtitle || event.thankYouText || ''))}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `event-${slug}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const PublicEventLandingPage: React.FC = () => {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const slug = (rawSlug || '').trim().toLowerCase();

  const [event, setEvent] = useState<OnlineEventData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [greetingName, setGreetingName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isDsgvoOpen, setIsDsgvoOpen] = useState(false);

  useEffect(() => {
    if (!slug) {
      setIsLoading(false);
      setLoadError('Kein Event-Slug in der URL.');
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    const unsubscribe = onSnapshot(
      doc(db, 'public_online_events', slug),
      (snapshot) => {
        if (!snapshot.exists()) {
          setEvent(null);
          setLoadError('Dieses Online-Event wurde nicht gefunden.');
        } else {
          const data = snapshot.data();
          if (data.isPublished === false) {
            setEvent(null);
            setLoadError('Dieses Event ist derzeit nicht freigeschaltet.');
          } else {
            const speakers = Array.isArray(data.speakers)
              ? data.speakers
                  .map((s: Record<string, unknown>) => ({
                    name: String(s?.name || '').trim(),
                    role: s?.role ? String(s.role) : undefined,
                    imageUrl: s?.imageUrl ? String(s.imageUrl) : undefined,
                    bio: s?.bio ? String(s.bio) : undefined,
                  }))
                  .filter((s: Speaker) => s.name)
              : [];
            const bullets = Array.isArray(data.bullets)
              ? data.bullets.map((b: unknown) => String(b)).filter(Boolean)
              : [];
            setEvent({
              title: String(data.title || 'Online-Event'),
              subtitle: data.subtitle ? String(data.subtitle) : undefined,
              date: data.date ? String(data.date) : undefined,
              time: data.time ? String(data.time) : undefined,
              format: data.format ? String(data.format) : 'Online-Livestream',
              teaser: data.teaser ? String(data.teaser) : undefined,
              bullets,
              heroImage: data.heroImage ? String(data.heroImage) : undefined,
              speakers,
              ctaLabel: data.ctaLabel ? String(data.ctaLabel) : 'Jetzt kostenfreien Platz sichern ❯',
              thankYouText: data.thankYouText ? String(data.thankYouText) : undefined,
              isPublished: data.isPublished !== false,
              activeDateISO: data.activeDateISO ? String(data.activeDateISO) : undefined,
              accessUrl: data.accessUrl ? String(data.accessUrl) : undefined,
              accessUrlEvening: data.accessUrlEvening ? String(data.accessUrlEvening) : undefined,
              willBeRecorded: data.willBeRecorded === true ? true : data.willBeRecorded === false ? false : null,
            });
            setLoadError(null);
          }
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('PublicEventLandingPage: Firestore-Fehler', error);
        setLoadError('Das Event konnte nicht geladen werden.');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [slug]);

  const updateField = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (submitError) setSubmitError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event || !slug) return;

    const email = form.email.trim().toLowerCase();
    const firstName = sanitizeFirstName(form.firstName);

    setSubmitError(null);
    const bad: string[] = [];
    if (!EMAIL_REGEX.test(email)) bad.push('email');
    setInvalidFields(bad);

    if (bad.length > 0) {
      setSubmitError('Bitte eine gültige E-Mail-Adresse eingeben.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        eventSlug: slug,
        email,
        userType: 'extern',
        tags: ['newsletter_active', `event:${slug}:registered`],
        source: 'landingpage_free_event',
        createdAt: serverTimestamp(),
        status: 'neu',
      };
      if (firstName) payload.firstName = firstName;

      await addDoc(collection(db, 'online_event_anmeldungen'), payload);

      try {
        await persistExternalLeadProfile(email, firstName);
      } catch (contactErr) {
        console.warn('Newsletter-Kontakt konnte nicht ergänzt werden (Anmeldung ist gespeichert):', contactErr);
      }

      const { weekday, dateLabel } = splitDateLabel(event.date, event.activeDateISO);
      const confirmMail = buildOnlineEventConfirmMail({
        firstName,
        title: event.title,
        slug,
        dateLabel: [weekday, dateLabel].filter(Boolean).join(', '),
        time: event.time,
        format: event.format,
        accessUrl: event.accessUrl,
        accessUrlEvening: event.accessUrlEvening,
        willBeRecorded: event.willBeRecorded,
      });

      try {
        await addDoc(collection(db, 'mail_queue'), {
          type: 'change_request',
          email,
          subject: confirmMail.subject,
          messageHtml: confirmMail.html,
          message: confirmMail.text,
          html: confirmMail.html,
          mailTitle: 'Online-Event Anmeldung',
          participantName: firstName || email,
          eventName: event.title,
          appBaseUrl: 'https://pe-one.friseur-coaching.de',
          status: 'pending',
          createdAt: serverTimestamp(),
        });
      } catch (mailErr) {
        console.warn('Bestätigungsmail an Teilnehmer fehlgeschlagen (Anmeldung ist gespeichert):', mailErr);
      }

      try {
        await addDoc(collection(db, 'mail_queue'), {
          type: 'change_request',
          email: 'erfolg@friseur-coaching.de',
          subject: `Online-Event Anmeldung: ${event.title}`,
          messageHtml: [
            `<p><strong>Neue Anmeldung</strong> für Online-Event <strong>${event.title}</strong> (Slug: ${slug}).</p>`,
            `<p>${firstName ? `${firstName} · ` : ''}${email}</p>`,
            `<p>Bestätigung an Teilnehmer: ${confirmMail.subject}</p>`,
          ].join(''),
          mailTitle: 'Online-Event Anmeldung',
          participantName: firstName || email,
          eventName: event.title,
          appBaseUrl: 'https://pe-one.friseur-coaching.de',
          status: 'pending',
          createdAt: serverTimestamp(),
        });
      } catch (mailErr) {
        console.warn('Admin-Hinweismail fehlgeschlagen (Anmeldung ist gespeichert):', mailErr);
      }

      setGreetingName(firstName);
      setSubmitSuccess(true);
      setForm(EMPTY_FORM);
    } catch (error) {
      console.error('PublicEventLandingPage: Anmeldung fehlgeschlagen', error);
      setSubmitError('Anmeldung fehlgeschlagen. Bitte erneut versuchen oder uns direkt kontaktieren.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: DEEP }}>
        <div className="flex items-center gap-3" style={{ color: GOLD }}>
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-sm font-bold uppercase tracking-widest">Event wird geladen…</span>
        </div>
      </div>
    );
  }

  if (loadError || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ backgroundColor: DEEP }}>
        <div
          className="max-w-md text-center border border-slate-800 rounded-2xl p-10"
          style={{ backgroundColor: SLATE }}
        >
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Event nicht verfügbar</h1>
          <p className="text-slate-400 text-sm">{loadError || 'Unbekannter Fehler.'}</p>
        </div>
      </div>
    );
  }

  const { weekday, dateLabel } = splitDateLabel(event.date, event.activeDateISO);
  const bullets = (event.bullets || []).map((item) => clarifyOnlineWording(item)).filter(Boolean);
  const speakers = event.speakers || [];
  const subtitle = clarifyOnlineWording(event.subtitle);
  const teaser = clarifyOnlineWording(event.teaser);
  const formatLabel = clarifyOnlineFormat(event.format);
  const formatType = formatLabel.replace(/^online[\s·-]+/i, '').trim() || 'Live-Meeting';

  return (
    <div className="min-h-screen text-slate-300 font-sans" style={{ backgroundColor: DEEP }}>
      <header className="fixed top-0 left-0 right-0 z-40 border-b border-slate-800/60 bg-[#0A0A0A]/90 backdrop-blur-sm px-4 md:px-8 py-4 md:py-5">
        <div className="max-w-5xl mx-auto flex items-center">
          <img
            src={PE_LOGO_SRC}
            alt="PreisExpert"
            className="h-36 md:h-44 w-auto max-w-full object-contain"
          />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 pt-48 md:pt-60 pb-10 md:pb-14">
        <section className="mb-12 md:mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <p
                className="inline-flex text-[10px] font-black uppercase tracking-[0.18em] mb-6 px-3 py-1.5 rounded-full border"
                style={{ color: GOLD, borderColor: `${GOLD}55` }}
              >
                Exklusives Online-Event für Friseurunternehmer | Kostenfrei
              </p>
              <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight leading-tight mb-4">
                {event.title}
              </h1>
              {subtitle && (
                <p className="text-lg md:text-xl text-slate-400 leading-relaxed mb-6 max-w-2xl whitespace-pre-wrap">
                  {subtitle}
                </p>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {weekday ? (
                  <div className="rounded-xl border border-slate-700 p-4" style={{ backgroundColor: CARD }}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Wochentag</p>
                    <p className="text-sm font-bold text-white">{weekday}</p>
                  </div>
                ) : null}
                {dateLabel ? (
                  <div className="rounded-xl border border-slate-700 p-4" style={{ backgroundColor: CARD }}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3" style={{ color: GOLD }} /> Datum
                    </p>
                    <p className="text-sm font-bold text-white">{dateLabel}</p>
                  </div>
                ) : null}
                {event.time ? (
                  <div className="rounded-xl border border-slate-700 p-4" style={{ backgroundColor: CARD }}>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" style={{ color: GOLD }} /> Uhrzeit
                    </p>
                    <p className="text-sm font-bold text-white">{event.time}</p>
                  </div>
                ) : null}
                <div className="rounded-xl border border-slate-700 p-4" style={{ backgroundColor: CARD }}>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
                    <Video className="w-3 h-3" style={{ color: GOLD }} /> Online
                  </p>
                  <p className="text-sm font-bold text-white">{formatType}</p>
                </div>
              </div>

              {(speakers.length > 0 || typeof event.willBeRecorded === 'boolean') ? (
                <div className="mb-2">
                  <h2 className="text-sm font-black uppercase tracking-widest mb-4" style={{ color: GOLD }}>
                    Referenten
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {(speakers.length > 0 ? speakers : [{ name: 'Das PreisExpert Team', role: 'Praxisnah · auf Augenhöhe' }]).map((speaker) => (
                      <div
                        key={speaker.name}
                        className="flex gap-4 p-4 rounded-2xl border border-slate-800"
                        style={{ backgroundColor: CARD }}
                      >
                        {speaker.imageUrl ? (
                          <img
                            src={speaker.imageUrl}
                            alt={speaker.name}
                            className="w-16 h-16 rounded-full object-cover border border-slate-700 shrink-0"
                          />
                        ) : (
                          <div
                            className="w-16 h-16 rounded-full flex items-center justify-center text-lg font-black shrink-0 border"
                            style={{ color: GOLD, borderColor: `${GOLD}40`, backgroundColor: DEEP }}
                          >
                            {speaker.name === 'Das PreisExpert Team' ? 'PE' : speaker.name.charAt(0)}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-white font-bold">{speaker.name}</p>
                          {speaker.role && (
                            <p className="text-xs uppercase tracking-wider mt-0.5" style={{ color: GOLD }}>
                              {speaker.role}
                            </p>
                          )}
                          {speaker.bio && (
                            <p className="text-slate-400 text-sm mt-2 leading-relaxed">{speaker.bio}</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {typeof event.willBeRecorded === 'boolean' && (
                      <EventRecordingBadge
                        recorded={event.willBeRecorded}
                        variant="tile"
                        showLiveOnlyHint
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div className="mb-2">
                  <h2 className="text-sm font-black uppercase tracking-widest mb-4" style={{ color: GOLD }}>
                    Referenten
                  </h2>
                  <div
                    className="flex gap-4 p-4 rounded-2xl border border-slate-800 max-w-md"
                    style={{ backgroundColor: CARD }}
                  >
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center text-lg font-black shrink-0 border"
                      style={{ color: GOLD, borderColor: `${GOLD}40`, backgroundColor: DEEP }}
                    >
                      PE
                    </div>
                    <div className="min-w-0">
                      <p className="text-white font-bold">Das PreisExpert Team</p>
                      <p className="text-xs uppercase tracking-wider mt-0.5" style={{ color: GOLD }}>
                        Praxisnah · auf Augenhöhe
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {teaser && (
                <p className="text-slate-300 text-base md:text-lg leading-relaxed whitespace-pre-line mt-6">
                  {teaser}
                </p>
              )}

              {bullets.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-sm font-black uppercase tracking-widest mb-4" style={{ color: GOLD }}>
                    Das erwartet dich
                  </h2>
                  <ul className="space-y-3">
                    {bullets.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-3 text-slate-300 text-sm md:text-base">
                        <span className="shrink-0 mt-0.5" style={{ color: GOLD }} aria-hidden>
                          ✦
                        </span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {event.heroImage ? (
              <div className="lg:col-span-6 order-1 lg:order-2 flex justify-center lg:justify-start self-start lg:pt-1">
                <AdaptiveEventImage src={event.heroImage} alt={event.title} />
              </div>
            ) : null}
          </div>
        </section>

        <section
          id="anmeldung"
          className="rounded-2xl border p-6 md:p-8 shadow-[0_0_40px_rgba(220,182,138,0.06)]"
          style={{ backgroundColor: SLATE, borderColor: `${GOLD}33` }}
        >
          <h2 className="text-xl font-bold text-white mb-1">Jetzt Platz sichern</h2>
          <p className="text-slate-500 text-sm mb-6">Kostenfrei · unverbindlich · Online</p>
          {event.willBeRecorded === true && (
            <RecordingLiveOnlyNotice className="mb-6" />
          )}

          {submitSuccess ? (
            <div className="text-center py-10 px-4 bg-emerald-950/30 border border-emerald-800/50 rounded-xl">
              <Check className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">{formatHalloGreeting(greetingName)}</h3>
              <p className="text-white text-base font-semibold mb-2">Dein Platz ist fest reserviert!</p>
              <p className="text-slate-300 text-sm leading-relaxed max-w-md mx-auto mb-2">
                {event.accessUrl
                  ? 'Der Zugangslink ist in deiner Bestätigungsmail (bitte auch Spam prüfen).'
                  : 'Eine Bestätigung geht an deine E-Mail. Den Zugangslink erhältst du, sobald er hinterlegt ist — spätestens in der Erinnerung 7 Tage vorher.'}
              </p>
              <p className="text-slate-500 text-xs leading-relaxed max-w-md mx-auto mb-6">
                Wir erinnern dich 7 Tage und 12 Stunden vor Beginn automatisch.
              </p>
              {event.willBeRecorded === true && (
                <RecordingLiveOnlyNotice className="max-w-md mx-auto mb-6 text-left" />
              )}
              {event.thankYouText ? (
                <p className="text-slate-400 text-sm leading-relaxed max-w-md mx-auto mb-6">{event.thankYouText}</p>
              ) : null}
              <button
                type="button"
                onClick={() => downloadEventIcs(slug, event)}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] text-[#0A0A0A]"
                style={{ backgroundColor: GOLD }}
              >
                <Download className="w-4 h-4" /> Termin in Kalender eintragen
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                  Vorname <span className="normal-case tracking-normal font-medium text-slate-500">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => updateField('firstName', e.target.value)}
                  className={inputClass}
                  autoComplete="given-name"
                  placeholder="z. B. Claudia"
                  maxLength={40}
                />
              </div>
              <div>
                <label className="block text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                  E-Mail-Adresse *
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={
                    invalidFields.includes('email')
                      ? `${inputClass} border-red-500 ring-1 ring-red-500`
                      : inputClass
                  }
                  autoComplete="email"
                  placeholder="deine@email.de"
                />
              </div>

              <p className="text-slate-400 text-xs leading-relaxed border-l-2 pl-3" style={{ borderColor: GOLD }}>
                Die Teilnahme an diesem exklusiven Online-Event ist kostenfrei. Als Gegenleistung erhältst du unseren
                PreisExpert-Praxis-Newsletter mit Praxistipps für Saloninhaber. Abmeldung jederzeit mit einem Klick
                möglich.{' '}
                <button
                  type="button"
                  onClick={() => setIsDsgvoOpen(true)}
                  className="underline underline-offset-2"
                  style={{ color: GOLD }}
                >
                  Datenschutz
                </button>
              </p>

              {submitError && (
                <p className="text-red-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 rounded-xl font-black uppercase tracking-widest text-xs text-[#0A0A0A] transition-opacity disabled:opacity-60"
                style={{ backgroundColor: GOLD }}
              >
                {isSubmitting ? (
                  <span className="inline-flex items-center gap-2 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" /> Wird gesendet…
                  </span>
                ) : (
                  event.ctaLabel || 'Jetzt kostenfreien Platz sichern ❯'
                )}
              </button>
            </form>
          )}
        </section>
      </main>

      <footer className="border-t border-slate-800/80 px-4 py-8 mt-8">
        <p className="text-center text-slate-600 text-xs">
          © {new Date().getFullYear()} PreisExpert · Online-Event
        </p>
      </footer>

      <DsgvoModal isOpen={isDsgvoOpen} onClose={() => setIsDsgvoOpen(false)} />
    </div>
  );
};

export default PublicEventLandingPage;
